import {
  applyClipArrangement,
  clipArrangementSnapshot,
  cloneInstrument,
  clonePreset,
  createDefaultInstruments,
  createDefaultSlotMixes,
  createDefaultSlotFxStacks,
  DEFAULT_INSTRUMENT_SLOT_COUNT,
  defaultPresetForSlot,
  ensureClipTrackCount,
  instrumentsFromPresets,
  MAX_INSTRUMENT_SLOT_COUNT,
  normalizeClipScenes,
  normalizeFxNode,
  normalizeInstrument,
  normalizeSlotMixes,
  normalizeSlotFxStacks,
  parsePatternString,
  presetsFromInstruments,
  stringifyPattern,
  type ClipIR,
  type FxNode,
  type Instrument,
  type InstrumentSlot,
  type Scene,
  type SlotMix,
  type SynthPreset,
} from "../../../../src/lib/audio";
import {
  clampFxHeight,
  clampInstrumentWidth,
  clampPatternsWidth,
  FX_HEIGHT_DEFAULT,
  INSTRUMENT_WIDTH_DEFAULT,
  PATTERNS_WIDTH_DEFAULT,
} from "./dockSizes";
import { LAB_DEMOS } from "./demos";
import { DEFAULT_MATERIAL, isMaterialMode, type MaterialMode } from "./material";
import { createDefaultMuteSolo, inferTrackCount, normalizeMuteSoloFlags } from "./muteSolo";
import { createDefaultBank, sanitizeBank, type NamedPreset } from "./presetBank";
import { createDefaultDrumBank, sanitizeDrumBank, type NamedDrumKit } from "./drumKitBank";
import { createDefaultWavetableBank, sanitizeWavetableBank, type NamedWavetablePreset } from "./wavetableBank";
import { createDefaultSamplerBank, sanitizeSamplerBank, type NamedSamplerPreset } from "./samplerBank";
import {
  sanitizePatternLoop,
  type PatternLoopRange,
} from "./patternLoop";
import { normalizeTrackColors, type TrackColor } from "./trackColors";
import { normalizeAdamBindings } from "../controls/controlPrefs";
import type { AdamControlBinding } from "../controls/controlRouter";

/** Legacy key name — keep stable so local drafts survive the Audio Lab → Adam rename. */
const STORAGE_KEY = "curva:audio-lab:v1";

export type LabArrangement = {
  scenes: Scene[];
  order: string[];
};

export type LabPersistedState = {
  version: 1;
  /** Pattern as DSL for compact, round-trippable storage. */
  pattern: string;
  /**
   * Scenes + song order (not in DSL yet). When missing, patterns migrate into S0.
   */
  arrangement?: LabArrangement;
  /**
   * Preferred: per-slot Instrument (synth | drums).
   * Legacy files may omit this and only have `presets`.
   */
  instruments?: Instrument[];
  /** Working copies currently loaded into slots 0..7 (synth mirror / legacy). */
  presets: SynthPreset[];
  /** Named preset library (factory eight first). */
  bank: NamedPreset[];
  /** Named drum-kit library. */
  drumBank?: NamedDrumKit[];
  /** Named wavetable preset library. */
  wavetableBank?: NamedWavetablePreset[];
  /** Named sampler preset library. */
  samplerBank?: NamedSamplerPreset[];
  /** Per-slot volume + pan (legacy files omit → defaults). */
  slotMix?: SlotMix[];
  /** Per-slot insert FX (legacy omit → empty). */
  slotFx?: FxNode[][];
  /** Tracker mute flags (length 8; legacy omit → all audible). */
  muted?: boolean[];
  /** Tracker solo flags (length 8; legacy omit → none soloed). */
  solo?: boolean[];
  /** Project-global track tints (legacy omit → none). */
  trackColors?: TrackColor[];
  fx: FxNode[];
  /**
   * MIDI / control learn bindings for this song draft.
   * Device/port prefs stay in curva:audio-lab:controls:v1.
   */
  controlBindings?: AdamControlBinding[];
  panels?: {
    fxOpen?: boolean;
    /** Bottom FX panel height in px. */
    fxHeight?: number;
    patternsOpen?: boolean;
    /** Arrange / Samples sidebar width in px. */
    patternsWidth?: number;
    /** Sample manager panel visibility. */
    samplesOpen?: boolean;
    activePatternId?: string;
    /** Loop brace — contiguous arrange range over clip.patterns. */
    patternLoop?: PatternLoopRange;
    activeSceneId?: string;
    /** Preferred key for left-dock slot (slot inspector). */
    instrumentSlot?: InstrumentSlot | null;
    /** @deprecated Read for migration; prefer instrumentSlot. */
    synthSlot?: InstrumentSlot | null;
    /** Left instrument sidebar width in px. */
    instrumentWidth?: number;
    /** Pattern DSL editor visibility. Default off. */
    dslOpen?: boolean;
    /** Status bar visibility. Default off. */
    statusOpen?: boolean;
    /** Keep playhead row scrolled to vertical center while playing. Default off. */
    followPlayhead?: boolean;
    /** Piano-key step entry (arm). Default off. */
    armMode?: boolean;
  };
  /** How much of the chrome wears the moulded-plastic material. */
  material: MaterialMode;
};

function sanitizeArrangement(raw: unknown): LabArrangement | undefined {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const obj = raw as Record<string, unknown>;
  if (!Array.isArray(obj.scenes) || obj.scenes.length === 0) {
    return undefined;
  }
  const scenes: Scene[] = [];
  for (const entry of obj.scenes) {
    if (!entry || typeof entry !== "object") {
      continue;
    }
    const scene = entry as Record<string, unknown>;
    if (typeof scene.id !== "string" || !scene.id.trim()) {
      continue;
    }
    const order = Array.isArray(scene.order)
      ? scene.order.filter((id): id is string => typeof id === "string")
      : [];
    scenes.push({
      id: scene.id.trim(),
      ...(typeof scene.name === "string" && scene.name.trim()
        ? { name: scene.name.trim().slice(0, 48) }
        : {}),
      order,
    });
  }
  if (scenes.length === 0) {
    return undefined;
  }
  const order = Array.isArray(obj.order)
    ? obj.order.filter((id): id is string => typeof id === "string")
    : scenes.map(scene => scene.id);
  return { scenes, order };
}

/** Resolve instruments from preferred array or legacy presets-only payload. */
export function resolvePersistedInstruments(
  instruments: unknown,
  presets: unknown,
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): Instrument[] {
  const n = Math.min(MAX_INSTRUMENT_SLOT_COUNT, Math.max(1, Math.round(count)));
  if (Array.isArray(instruments) && instruments.some(entry => entry != null)) {
    return Array.from({ length: n }, (_, i) =>
      normalizeInstrument(instruments[i], i as InstrumentSlot),
    );
  }
  if (Array.isArray(presets)) {
    const synthPresets = Array.from({ length: n }, (_, i) =>
      presets[i] && typeof presets[i] === "object"
        ? clonePreset(presets[i] as SynthPreset)
        : defaultPresetForSlot(i),
    );
    return instrumentsFromPresets(synthPresets, n);
  }
  return createDefaultInstruments(n);
}

function defaultState(): LabPersistedState {
  const chip = LAB_DEMOS.find(demo => demo.id === "chip") ?? LAB_DEMOS[0]!;
  const loaded = chip.load();
  normalizeClipScenes(loaded.clip);
  const instruments = loaded.instruments ?? instrumentsFromPresets(loaded.presets);
  const trackCount = instruments.length;
  return {
    version: 1,
    pattern: stringifyPattern(loaded.clip),
    arrangement: clipArrangementSnapshot(loaded.clip),
    instruments,
    presets: presetsFromInstruments(instruments),
    bank: loaded.bank ?? createDefaultBank(),
    drumBank: loaded.drumBank ?? createDefaultDrumBank(),
    wavetableBank: createDefaultWavetableBank(),
    samplerBank: createDefaultSamplerBank(),
    slotMix: loaded.slotMix ?? createDefaultSlotMixes(trackCount),
    slotFx: loaded.slotFx ?? createDefaultSlotFxStacks(trackCount),
    muted: loaded.muted ?? createDefaultMuteSolo(trackCount),
    solo: loaded.solo ?? createDefaultMuteSolo(trackCount),
    trackColors: normalizeTrackColors(loaded.trackColors, trackCount),
    fx: loaded.fx,
    controlBindings: [],
    panels: {
      fxOpen: false,
      fxHeight: FX_HEIGHT_DEFAULT,
      patternsOpen: false,
      patternsWidth: PATTERNS_WIDTH_DEFAULT,
      samplesOpen: false,
      activePatternId: loaded.clip.patterns[0]?.id ?? "P0",
      patternLoop: sanitizePatternLoop({ start: 0, length: 1 }, loaded.clip.patterns.length),
      activeSceneId: loaded.clip.scenes[0]?.id ?? "S0",
      instrumentSlot: null,
      synthSlot: null,
      instrumentWidth: INSTRUMENT_WIDTH_DEFAULT,
      dslOpen: false,
      statusOpen: false,
      followPlayhead: false,
      armMode: false,
    },
    material: DEFAULT_MATERIAL,
  };
}

export function loadLabState(): LabPersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultState();
    }
    const parsed = JSON.parse(raw) as Partial<LabPersistedState>;
    if (parsed.version !== 1 || typeof parsed.pattern !== "string") {
      return defaultState();
    }
    const { clip, warnings } = parsePatternString(parsed.pattern);
    if (warnings.length > 0 && clip.patterns.length === 0) {
      return defaultState();
    }
    applyClipArrangement(clip, sanitizeArrangement(parsed.arrangement));
    const trackCount = inferTrackCount({
      instruments: parsed.instruments,
      presets: parsed.presets,
      muted: parsed.muted,
      solo: parsed.solo,
      slotMix: parsed.slotMix,
      slotFx: parsed.slotFx,
      patternTrackCount: Math.max(0, ...clip.patterns.map(p => p.tracks.length)),
    });
    ensureClipTrackCount(clip, trackCount);
    const instruments = resolvePersistedInstruments(
      parsed.instruments,
      parsed.presets,
      trackCount,
    );
    const presets = presetsFromInstruments(instruments);
    const fx = Array.isArray(parsed.fx)
      ? parsed.fx
        .filter(node => node && typeof node === "object" && typeof (node as FxNode).type === "string")
        .map(node => normalizeFxNode(node as FxNode))
      : [];
    const rawSlot = parsed.panels?.instrumentSlot ?? parsed.panels?.synthSlot;
    const instrumentSlot =
      typeof rawSlot === "number"
      && Number.isInteger(rawSlot)
      && rawSlot >= 0
      && rawSlot < trackCount
        ? rawSlot as InstrumentSlot
        : null;
    const rawActive = parsed.panels?.activePatternId;
    const activePatternId =
      typeof rawActive === "string" && rawActive.trim()
        ? rawActive.trim()
        : "P0";
    const activeIndex = Math.max(0, clip.patterns.findIndex(p => p.id === activePatternId));
    const patternLoop = sanitizePatternLoop(
      parsed.panels?.patternLoop ?? { start: activeIndex, length: 1 },
      clip.patterns.length,
    );
    const rawScene = parsed.panels?.activeSceneId;
    const activeSceneId =
      typeof rawScene === "string" && clip.scenes.some(scene => scene.id === rawScene)
        ? rawScene
        : (clip.scenes[0]?.id ?? "S0");
    return {
      version: 1,
      pattern: stringifyPattern(clip),
      arrangement: clipArrangementSnapshot(clip),
      instruments,
      presets,
      bank: sanitizeBank(parsed.bank),
      drumBank: sanitizeDrumBank(parsed.drumBank),
      wavetableBank: sanitizeWavetableBank(parsed.wavetableBank),
      samplerBank: sanitizeSamplerBank(parsed.samplerBank),
      slotMix: normalizeSlotMixes(parsed.slotMix, trackCount),
      slotFx: normalizeSlotFxStacks(parsed.slotFx, trackCount),
      muted: normalizeMuteSoloFlags(parsed.muted, trackCount),
      solo: normalizeMuteSoloFlags(parsed.solo, trackCount),
      trackColors: normalizeTrackColors(parsed.trackColors, trackCount),
      fx,
      controlBindings: normalizeAdamBindings(parsed.controlBindings),
      panels: {
        fxOpen: Boolean(parsed.panels?.fxOpen),
        fxHeight: clampFxHeight(parsed.panels?.fxHeight),
        patternsOpen: Boolean(parsed.panels?.patternsOpen),
        patternsWidth: clampPatternsWidth(parsed.panels?.patternsWidth),
        samplesOpen: Boolean(parsed.panels?.samplesOpen),
        activePatternId,
        patternLoop,
        activeSceneId,
        dslOpen: Boolean(parsed.panels?.dslOpen),
        statusOpen: Boolean(parsed.panels?.statusOpen),
        followPlayhead: Boolean(parsed.panels?.followPlayhead),
        armMode: Boolean(parsed.panels?.armMode),
        instrumentSlot,
        synthSlot: instrumentSlot,
        instrumentWidth: clampInstrumentWidth(parsed.panels?.instrumentWidth),
      },
      material: isMaterialMode(parsed.material) ? parsed.material : DEFAULT_MATERIAL,
    };
  } catch {
    return defaultState();
  }
}

export function clipFromLabState(state: LabPersistedState): ClipIR {
  const clip = parsePatternString(state.pattern).clip;
  applyClipArrangement(clip, state.arrangement);
  return clip;
}

export type LabPersistPartial = {
  clip?: ClipIR;
  instruments?: Instrument[];
  presets?: SynthPreset[];
  bank?: NamedPreset[];
  drumBank?: NamedDrumKit[];
  wavetableBank?: NamedWavetablePreset[];
  samplerBank?: NamedSamplerPreset[];
  slotMix?: SlotMix[];
  slotFx?: FxNode[][];
  muted?: boolean[];
  solo?: boolean[];
  trackColors?: TrackColor[];
  fx?: FxNode[];
  controlBindings?: AdamControlBinding[];
  panels?: LabPersistedState["panels"];
  material?: MaterialMode;
};

export function saveLabState(partial: LabPersistPartial): void {
  const current = loadLabState();
  const clip = partial.clip;
  if (clip) {
    normalizeClipScenes(clip);
  }

  let instruments = current.instruments ?? resolvePersistedInstruments(undefined, current.presets);
  if (partial.instruments) {
    instruments = partial.instruments.map(cloneInstrument);
  } else if (partial.presets) {
    // Bank / synth writes: update synth slots only; keep drum kits intact.
    const n = Math.max(instruments.length, partial.presets.length);
    const fromPresets = instrumentsFromPresets(partial.presets.map(clonePreset), n);
    instruments = Array.from({ length: n }, (_, i) => {
      const prev = instruments[i];
      if (prev?.kind === "drums" || prev?.kind === "sf2" || prev?.kind === "wavetable" || prev?.kind === "sampler") {
        return cloneInstrument(prev);
      }
      return fromPresets[i] ?? createDefaultInstruments(n)[i]!;
    });
  }

  const trackCount = instruments.length || DEFAULT_INSTRUMENT_SLOT_COUNT;
  if (clip) {
    ensureClipTrackCount(clip, trackCount);
  }

  const next: LabPersistedState = {
    version: 1,
    pattern: clip ? stringifyPattern(clip) : current.pattern,
    arrangement: clip ? clipArrangementSnapshot(clip) : current.arrangement,
    instruments,
    presets: presetsFromInstruments(instruments),
    bank: partial.bank ? sanitizeBank(partial.bank) : current.bank,
    drumBank: partial.drumBank ? sanitizeDrumBank(partial.drumBank) : (current.drumBank ?? createDefaultDrumBank()),
    wavetableBank: partial.wavetableBank
      ? sanitizeWavetableBank(partial.wavetableBank)
      : (current.wavetableBank ?? createDefaultWavetableBank()),
    samplerBank: partial.samplerBank
      ? sanitizeSamplerBank(partial.samplerBank)
      : (current.samplerBank ?? createDefaultSamplerBank()),
    slotMix: partial.slotMix
      ? normalizeSlotMixes(partial.slotMix, trackCount)
      : normalizeSlotMixes(current.slotMix, trackCount),
    slotFx: partial.slotFx
      ? normalizeSlotFxStacks(partial.slotFx, trackCount)
      : normalizeSlotFxStacks(current.slotFx, trackCount),
    muted: partial.muted
      ? normalizeMuteSoloFlags(partial.muted, trackCount)
      : normalizeMuteSoloFlags(current.muted, trackCount),
    solo: partial.solo
      ? normalizeMuteSoloFlags(partial.solo, trackCount)
      : normalizeMuteSoloFlags(current.solo, trackCount),
    trackColors: partial.trackColors
      ? normalizeTrackColors(partial.trackColors, trackCount)
      : normalizeTrackColors(current.trackColors, trackCount),
    fx: partial.fx ? partial.fx.map(fx => normalizeFxNode(fx)) : current.fx,
    controlBindings: partial.controlBindings !== undefined
      ? normalizeAdamBindings(partial.controlBindings)
      : normalizeAdamBindings(current.controlBindings),
    panels: partial.panels
      ? { ...current.panels, ...partial.panels }
      : current.panels,
    material: partial.material ?? current.material,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota / private mode — ignore
  }
}

/** Debounced save helper for slider drags. */
export function createLabStateSaver(delayMs = 250): {
  schedule: (partial: LabPersistPartial) => void;
  flush: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: LabPersistPartial = {};

  return {
    schedule(partial: LabPersistPartial) {
      pending = {
        ...pending,
        ...partial,
        panels: partial.panels
          ? { ...pending.panels, ...partial.panels }
          : pending.panels,
      };
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        timer = undefined;
        const toSave = pending;
        pending = {};
        saveLabState(toSave);
      }, delayMs);
    },
    flush() {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
      if (Object.keys(pending).length > 0) {
        const toSave = pending;
        pending = {};
        saveLabState(toSave);
      }
    },
  };
}
